import React, { useEffect, useState } from "react";
import cs from "classnames";

import { getWorkplaceIcon } from "../../Workplaces/helpers/getWorkplaceInformation";
import { DeviceTreeNode, hasSelectedDevice } from "./lib/deviceTree";

import { Icon } from "../../shared/Icon/Icon";

import "../../../views/Manage/Workplaces/WorkplacesTree/WorkplacesTreeLevel/WorkplacesTreeLevel.scss";

interface Props {
  node: DeviceTreeNode;
  selectedIds: ReadonlySet<string>;
  onToggleSelection: (node: DeviceTreeNode) => void;
  allToggled: boolean;
}

export const DeviceTreeLevel = ({
  node,
  selectedIds,
  onToggleSelection,
  allToggled,
}: Props) => {
  const [open, setOpen] = useState(allToggled);

  // "Expand All" / "Collapse All" override each level's own toggle
  useEffect(() => {
    setOpen(allToggled);
  }, [allToggled]);

  const hasChildren = node.children.length > 0;
  const isSelectable = node.devices.length > 0;
  const isSelected = isSelectable && hasSelectedDevice(node, selectedIds);
  const icon =
    node.type === "Unassigned" ? "pin-slash-icon" : getWorkplaceIcon(node.type);

  const label = (
    <>
      <span className="WorkplacesTreeLevel__icon">
        <Icon icon={icon} />
      </span>
      {/* Long names are cut off with an ellipsis */}
      <span className="WorkplacesTreeLevel__name" title={node.name}>
        {node.name}
      </span>
    </>
  );

  return (
    <>
      <div
        className={cs("WorkplacesTreeLevel", "DeviceTreeLevel", {
          "DeviceTreeLevel--unassigned": node.type === "Unassigned",
          "WorkplacesTreeLevel--open": open,
          "WorkplacesTreeLevel--space": !hasChildren,
        })}
      >
        <div className="WorkplacesTreeLevel__wrapper">
          {hasChildren ? (
            <button
              type="button"
              aria-label={`${open ? "Collapse" : "Expand"} ${node.name}`}
              aria-expanded={open}
              className="WorkplacesTreeLevel__toggle"
              onClick={() => setOpen((prev) => !prev)}
            />
          ) : null}

          {isSelectable ? (
            <button
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              className="WorkplacesTreeLevel__link DeviceTreeLevel__select"
              onClick={() => onToggleSelection(node)}
            >
              {label}
              <span className="WorkplacesTreeLevel__status">
                <Icon icon={isSelected ? "checkbox-full" : "checkbox-empty"} />
              </span>
            </button>
          ) : (
            <div className="WorkplacesTreeLevel__link">{label}</div>
          )}
        </div>
      </div>

      {hasChildren ? (
        <div
          className={cs("WorkplacesTreeLevel__sub-level", {
            "WorkplacesTreeLevel__sub-level--open": open,
          })}
        >
          {node.children.map((child) => (
            <DeviceTreeLevel
              key={child.id}
              node={child}
              selectedIds={selectedIds}
              onToggleSelection={onToggleSelection}
              allToggled={allToggled}
            />
          ))}
        </div>
      ) : null}
    </>
  );
};
